import { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { Paper, Tabs, Tab, useTheme } from '@mui/material';

import TabPanel from '../../components/TabPanel';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import Title from '../../components/Title';
import ForbiddenPage from '../../components/ForbiddenPage';
import CompaniesManager from '../../components/CompaniesManager';
import PlansManager from '../../components/PlansManager';
import Options from '../../components/Settings/Options.jsx';
import OnlyForSuperUser from '../../components/OnlyForSuperUser';

import { i18n } from '../../translate/i18n.js';
import { AuthContext } from '../../context/Auth/AuthContext.jsx';
import useCompanySettings from '../../hooks/useSettings/companySettings.js';
import useSettings from '../../hooks/useSettings/index.js';
import { getStyled } from './styles.js';

const SettingsPage = () => {
  const theme = useTheme();
  const classes = getStyled(theme);
  const [tab, setTab] = useState('options');
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});

  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    let isMounted = true;

    async function findData() {
      try {
        const companyId = user.companyId;
        const [settingList, settingListOld] = await Promise.all([
          getAllSettings(companyId),
          getAllSettingsOld(),
        ]);

        if (!isMounted) return;

        setSettings(settingList);
        setOldSettings(settingListOld);
        setCurrentUser(user);
      } catch (e) {
        if (isMounted) toastError(e);
      }
    }

    findData();

    return () => {
      isMounted = false;
    };
  }, [user.companyId]); // eslint-disable-line

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const isSuper = () => currentUser.super;

  return (
    <MainContainer sx={classes.root}>
      {user.profile === 'user' ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>{i18n.t('settings.title')}</Title>
          </MainHeader>
          <Paper sx={classes.mainPaper} variant='outlined'>
            <Tabs
              value={tab}
              indicatorColor='primary'
              textColor='primary'
              scrollButtons='on'
              variant='scrollable'
              onChange={handleTabChange}
              sx={classes.tab}
            >
              <Tab label={i18n.t('settings.tabs.options')} value='options' />
              {isSuper() && (
                <Tab
                  label={i18n.t('settings.tabs.companies')}
                  value='companies'
                />
              )}
              {isSuper() && (
                <Tab label={i18n.t('settings.tabs.plans')} value='plans' />
              )}
            </Tabs>
            <Paper sx={classes.paper} elevation={0}>
              <OnlyForSuperUser
                user={currentUser}
                yes={() => (
                  <>
                    <TabPanel
                      sx={classes.container}
                      value={tab}
                      name='companies'
                    >
                      <CompaniesManager />
                    </TabPanel>
                    <TabPanel sx={classes.container} value={tab} name='plans'>
                      <PlansManager />
                    </TabPanel>
                  </>
                )}
              />
              <TabPanel sx={classes.container} value={tab} name='options'>
                <Options
                  settings={settings}
                  oldSettings={oldSettings}
                  user={currentUser}
                />
              </TabPanel>
            </Paper>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default SettingsPage;
